'use client';

import { use, useMemo, useState, useRef, useEffect } from 'react';
import gsap from 'gsap';
import { Container, Text, useResizeObserver, useTranslation, useUi } from '@weareconceptstudio/core';

//* Libs
import { fetchData } from '@/libs/get-data';

//* Styles
import FaqTemplateStyle from './style';

//* Components
import FaqItem from '../Item';
import Tab from '@/components/Tab';
import Editor from '@/components/Editor';
import CallToAction from '@/modules/_sections/CallToAction';
import TitleAnim from '@/modules/_animation/Title';

const FaqTemplate = ({ title, description }) => {
	const { selectedLang } = useTranslation();
	const { winWidth } = useUi();

	//! Refs
	const contentItemRefs = useRef([]);
	const descriptionRef = useRef();
	const tabRefWrapper = useRef();

	//! States
	const [activeTab, setActiveTab] = useState(null);
	const [dimensions, setDimensions] = useState({
		headerHeight: 0,
		distance: 0,
		stickyWrapHeight: 0,
	});

	const { headerHeight, distance, stickyWrapHeight } = dimensions;

	const data = use(fetchData({ name: 'faqs', lang: selectedLang }));

	const resizes = useResizeObserver(typeof window !== 'undefined' ? document.body : null);

	//! Mobile Checks for FAQ
	const isMobile = useMemo(() => winWidth < 1024, [winWidth]);

	//! Set Elements Sizes
	useEffect(() => {
		if (typeof window === 'undefined') return;

		const getElementSize = (selector, prop = 'clientHeight') => document.querySelector(selector)?.[prop] ?? 0;

		const wrapperEl = document.querySelector('.faq-template-wrapper');
		const distance = wrapperEl ? parseInt(getComputedStyle(wrapperEl).marginBlockStart || 0) : 0;

		setDimensions({
			headerHeight: getElementSize('header'),
			distance,
			stickyWrapHeight: getElementSize('.faq-sticky-wrapper'),
		});
	}, [resizes, winWidth]);

	//! Scroll Active logic with IntersectionObserver
	useEffect(() => {
		if (!contentItemRefs.current.length) return;

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						const index = contentItemRefs.current.findIndex((ref) => ref === entry.target);

						if (index !== -1) {
							setActiveTab(data[index].name);
						}
					}
				});
			},
			{
				root: null,
				rootMargin: `-${headerHeight + distance}px 0px -50% 0px`,
				threshold: 0.1,
			}
		);

		const refsSnapshot = [...contentItemRefs.current];
		refsSnapshot.forEach((ref) => ref && observer.observe(ref));

		return () => {
			refsSnapshot.forEach((ref) => ref && observer.unobserve(ref));
		};
	}, [data, headerHeight, distance]);

	//! Sticky logic
	useEffect(() => {
		const matchMedia = gsap.matchMedia();

		if (resizes && typeof window !== 'undefined') {
			matchMedia.add('(min-width: 1024px)', () => {
				const sticky = document.querySelector('.faq-sticky-wrapper');
				const leftBar = document.querySelector('.faq-left-bar');

				if (!sticky || !leftBar) return;

				gsap.to(sticky, {
					scrollTrigger: {
						trigger: leftBar,
						start: `top ${headerHeight + distance}px`,
						end: `bottom ${headerHeight + distance + stickyWrapHeight}px`,
						pin: sticky,
						pinSpacing: false,
						// markers: true,
					},
				});
			});
		}

		return () => matchMedia.revert();
	}, [resizes, headerHeight, distance, stickyWrapHeight, winWidth]);

	//! Scroll to Content
	const scrollToContent = (tabName) => {
		const contentIndex = data.findIndex((item) => item.name === tabName);

		if (contentIndex === -1 || !contentItemRefs.current[contentIndex]) return;

		const contentElement = contentItemRefs.current[contentIndex];

		const offset = headerHeight + distance;

		setActiveTab(tabName);

		gsap.to(window, {
			duration: 0.7,
			scrollTo: {
				y: contentElement,
				offsetY: offset,
				autoKill: false,
			},
			ease: 'power2.inOut',
		});
	};

	//! Animate Faq
	useEffect(() => {
		if (descriptionRef.current) {
			gsap.fromTo(descriptionRef.current, { opacity: 0 }, { opacity: 1, duration: 0.5, ease: 'power2.in', delay: 0.6 });
		}
		if (tabRefWrapper.current?.children?.length) {
			gsap.fromTo(tabRefWrapper.current.children, { scale: 0 }, { scale: 1, duration: 0.6, ease: 'power2.out', delay: 0.7, stagger: 0.1 });
		}

		return () => {
			if (descriptionRef.current) {
				gsap.killTweensOf(descriptionRef.current);
			}
			if (tabRefWrapper.current?.children?.length) {
				gsap.killTweensOf(tabRefWrapper.current.children);
			}
		};
	}, [isMobile]);

	const tabContent = useMemo(() => {
		return data.map((item, index) => (
			<FaqItem
				isFirst={index == 0}
				key={item.name}
				data={item}
				ref={(ref) => (contentItemRefs.current[index] = ref)}
			/>
		));
	}, [data]);

	if (!data.length) return null;

	return (
		<FaqTemplateStyle className='faq-template-wrapper'>
			<Container
				row
				isSection
				className='faq-version'
			>
				<div className='col-4 col-dx-12 faq-left-bar'>
					<div className='faq-sticky-wrapper'>
						<TitleAnim load={true}>
							<Text
								tag={'h1'}
								className={'h2 font-larken-light green-1000 title-faq'}
							>
								{title}
							</Text>
						</TitleAnim>
						<div
							ref={descriptionRef}
							className='wrapper-description opacity-zero'
						>
							<Editor
								data={description}
								className={'desc-faq'}
							/>
						</div>

						{isMobile ? null : (
							<div
								ref={tabRefWrapper}
								className='faq-tab-wrapper'
							>
								{data.map((item) => (
									<Tab
										key={item.name}
										text={item.name}
										active={item.name === activeTab}
										onClick={() => scrollToContent(item.name)}
									/>
								))}
							</div>
						)}
					</div>
				</div>

				<div className='empty-col col-1 col-dx-12' />

				<div className='col-7 col-dx-12 faq-items'>
					<div className='faq-inner-items'>{tabContent}</div>
				</div>
			</Container>

			<CallToAction />
		</FaqTemplateStyle>
	);
};

export default FaqTemplate;
